import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { useEffect, useState, useMemo } from 'react';
import useFetchIam from '@/features/agent/api/useFetchIam';
import useFetchCustomCategories from '@/features/agent/customize/api/useFetchCustomCategories';
import useFetchCustomCategory from '@/features/agent/customize/api/useFetchCustomCategory';
import useCreateCustomCategory from '@/features/agent/customize/api/useCreateCustomCategory';
import useUpdateCustomCategory from '@/features/agent/customize/api/useUpdateCustomCategory';
import useDeleteCustomCategory from '@/features/agent/customize/api/useDeleteCustomCategory';
import useReorderCustomCategories from '@/features/agent/customize/api/useReorderCustomCategories';
import useUpdateCustomField from '@/features/agent/customize/api/useUpdateCustomField';
import useCreateCustomField from '@/features/agent/customize/api/useCreateCustomField';
import useDeleteCustomField from '@/features/agent/customize/api/useDeleteCustomField';
import useReorderCustomFields from '@/features/agent/customize/api/useReorderCustomFields';
import CategoryTabs from '@/features/agent/customize/components/CategoryTabs';
import AddCategoryModal from '@/features/agent/customize/components/AddCategoryModal';
import EditCategoryModal from '@/features/agent/customize/components/EditCategoryModal';
import AddCustomFieldModal from '@/features/agent/customize/components/AddCustomFieldModal';
import EditCustomFieldModal from '@/features/agent/customize/components/EditCustomFieldModal';
import CustomFieldList from '@/features/agent/customize/components/CustomFieldList';
import DeleteCustomFieldDialog from '@/features/agent/customize/components/DeleteCustomFieldDialog';
import DeleteCustomCategoryDialog from '@/features/agent/customize/components/DeleteCustomCategoryDialog';
import { showSuccessToast, showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import type { CustomField, CustomFieldType } from '@/features/agent/customize/types';
import Loading from '@/components/Loading';
import { Button } from '@/components/Button';

// クエリパラメータのスキーマ
const customizeSearchSchema = z.object({
  type: z
    .enum(['candidate_profile', 'company', 'candidate_interview_log'])
    .optional()
    .default('candidate_profile'),
});

export const Route = createFileRoute('/agent/customize')({
  component: Customize,
  staticData: { title: 'カスタマイズ' },
  validateSearch: customizeSearchSchema,
});

function Customize() {
  const { type } = Route.useSearch();
  const { data: iam } = useFetchIam();
  const { data: categories, isLoading } = useFetchCustomCategories(type);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
  const [isEditFieldModalOpen, setIsEditFieldModalOpen] = useState(false);
  const [fieldToEdit, setFieldToEdit] = useState<CustomField | null>(null);
  const [fieldToDelete, setFieldToDelete] = useState<CustomField | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: number; name: string } | null>(
    null
  );
  const createCategoryMutation = useCreateCustomCategory();
  const updateCategoryMutation = useUpdateCustomCategory();
  const deleteCategoryMutation = useDeleteCustomCategory();
  const reorderCategoriesMutation = useReorderCustomCategories();
  const createFieldMutation = useCreateCustomField();
  const updateFieldMutation = useUpdateCustomField();
  const deleteFieldMutation = useDeleteCustomField();
  const reorderFieldsMutation = useReorderCustomFields();

  // 選択中のカテゴリ詳細を取得
  const { data: categoryDetail, isLoading: isLoadingDetail } =
    useFetchCustomCategory(selectedCategoryId);

  // カスタムフィールドをorder_number順にソート
  const sortedFields = useMemo(() => {
    if (!categoryDetail?.custom_fields) return [];
    return [...categoryDetail.custom_fields].sort((a, b) => a.order_number - b.order_number);
  }, [categoryDetail]);

  // 初回ロード時に管理者権限チェック
  useEffect(() => {
    if (iam && !iam.current_user.is_admin) {
      showErrorToast('管理者権限が必要です。この機能にアクセスできません。');
    }
  }, [iam]);

  // 初回ロード時に最初のカテゴリを選択
  useEffect(() => {
    if (categories && categories.length > 0 && !selectedCategoryId) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);

  const handleAddCategory = () => {
    setIsAddModalOpen(true);
  };

  const handleEditCategory = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleSubmitNewCategory = async (name: string) => {
    try {
      await createCategoryMutation.mutateAsync({ name, type });
      setIsAddModalOpen(false);
      showSuccessToast('区分を追加しました');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '区分の追加に失敗しました';
      showErrorToast(errorMessage);
    }
  };

  const handleSubmitEditCategory = async (id: number, name: string) => {
    try {
      await updateCategoryMutation.mutateAsync({ id, name, type });
      setIsEditModalOpen(false);
      showSuccessToast('区分を更新しました');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '区分の更新に失敗しました';
      showErrorToast(errorMessage);
    }
  };

  // カテゴリの順序変更用のヘルパー関数
  const getCategoryPosition = () => {
    if (!categories || !selectedCategoryId)
      return { index: -1, canMoveUp: false, canMoveDown: false };
    const index = categories.findIndex((cat) => cat.id === selectedCategoryId);
    return {
      index,
      canMoveUp: index > 0,
      canMoveDown: index >= 0 && index < categories.length - 1,
    };
  };

  const handleMoveCategoryUp = async () => {
    if (!categories || !selectedCategoryId) return;
    const { index } = getCategoryPosition();
    if (index <= 0) return;

    try {
      const sortedCategories = [...categories].sort((a, b) => a.orderNumber - b.orderNumber);

      // 現在のカテゴリと前のカテゴリを入れ替え
      const newOrder = [...sortedCategories];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];

      // 新しい順序に基づいてorder_numberを1から順番に再割り当て
      const categoryOrders = newOrder.map((cat, i) => ({
        id: cat.id,
        order_number: i + 1,
      }));

      const recordClassName = sortedCategories[index].recordClassName;
      await reorderCategoriesMutation.mutateAsync({
        recordClassName,
        categoryOrders,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '順序の変更に失敗しました';
      showErrorToast(errorMessage);
    }
  };

  const handleMoveCategoryDown = async () => {
    if (!categories || !selectedCategoryId) return;
    const { index } = getCategoryPosition();
    if (index < 0 || index >= categories.length - 1) return;

    try {
      const sortedCategories = [...categories].sort((a, b) => a.orderNumber - b.orderNumber);

      // 現在のカテゴリと次のカテゴリを入れ替え
      const newOrder = [...sortedCategories];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];

      // 新しい順序に基づいてorder_numberを1から順番に再割り当て
      const categoryOrders = newOrder.map((cat, i) => ({
        id: cat.id,
        order_number: i + 1,
      }));

      const recordClassName = sortedCategories[index].recordClassName;
      await reorderCategoriesMutation.mutateAsync({
        recordClassName,
        categoryOrders,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '順序の変更に失敗しました';
      showErrorToast(errorMessage);
    }
  };

  const handleAddField = () => {
    setIsAddFieldModalOpen(true);
  };

  const handleEditField = (field: CustomField) => {
    setFieldToEdit(field);
    setIsEditFieldModalOpen(true);
  };

  const handleDeleteField = (field: CustomField) => {
    setFieldToDelete(field);
  };

  const handleConfirmDelete = async () => {
    if (!fieldToDelete || !selectedCategoryId) return;

    try {
      await deleteFieldMutation.mutateAsync({
        customFieldId: fieldToDelete.id,
        categoryId: selectedCategoryId,
      });
      setFieldToDelete(null);
      showSuccessToast('カスタム項目を削除しました');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'カスタム項目の削除に失敗しました';
      showErrorToast(errorMessage);
    }
  };

  const handleCancelDelete = () => {
    setFieldToDelete(null);
  };

  const handleDeleteCategory = () => {
    if (!selectedCategoryId || !categoryDetail) return;
    setCategoryToDelete({ id: selectedCategoryId, name: categoryDetail.name });
  };

  const handleConfirmDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      await deleteCategoryMutation.mutateAsync({
        categoryId: categoryToDelete.id,
        recordClassName: type,
      });
      setCategoryToDelete(null);
      setSelectedCategoryId(null);
      showSuccessToast('区分を削除しました');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '区分の削除に失敗しました';
      showErrorToast(errorMessage);
    }
  };

  const handleCancelDeleteCategory = () => {
    setCategoryToDelete(null);
  };

  const handleCloseAddFieldModal = () => {
    setIsAddFieldModalOpen(false);
  };

  const handleCloseEditFieldModal = () => {
    setIsEditFieldModalOpen(false);
    setFieldToEdit(null);
  };

  const handleSubmitAddField = async (params: {
    name: string;
    field_type: CustomFieldType;
    is_required: boolean;
    unit_name?: string | null;
    options?: string[];
  }) => {
    if (!selectedCategoryId) return;

    try {
      await createFieldMutation.mutateAsync({
        categoryId: selectedCategoryId,
        name: params.name,
        field_type: params.field_type,
        is_required: params.is_required,
        unit_name: params.unit_name,
        options: params.options,
      });
      setIsAddFieldModalOpen(false);
      showSuccessToast('カスタム項目を追加しました');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'カスタム項目の追加に失敗しました';
      showErrorToast(errorMessage);
    }
  };

  const handleSubmitEditField = async (params: {
    name: string;
    field_type: string;
    is_required: boolean;
    unit_name?: string | null;
    options?: string[];
  }) => {
    if (!fieldToEdit || !selectedCategoryId) return;

    try {
      await updateFieldMutation.mutateAsync({
        customFieldId: fieldToEdit.id,
        categoryId: selectedCategoryId,
        name: params.name,
        field_type: params.field_type as CustomFieldType,
        is_required: params.is_required,
        unit_name: params.unit_name,
        // 選択肢を配列で送信（バックエンドで配列と文字列両方に対応）
        options: params.options,
      });
      setIsEditFieldModalOpen(false);
      setFieldToEdit(null);
      showSuccessToast('カスタム項目を更新しました');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'カスタム項目の更新に失敗しました';
      showErrorToast(errorMessage);
    }
  };

  const handleReorderFields = async (reorderedFields: CustomField[]) => {
    if (!selectedCategoryId) return;

    try {
      // 新しい順序でorder_numberを設定
      const fieldOrders = reorderedFields.map((field, index) => ({
        id: field.id,
        order_number: index + 1,
      }));

      await reorderFieldsMutation.mutateAsync({
        categoryId: selectedCategoryId,
        fieldOrders,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'カスタム項目の順序更新に失敗しました';
      showErrorToast(errorMessage);
    }
  };

  // 管理者権限チェック
  if (iam && !iam.current_user.is_admin) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <p className="font-bold text-red-700">アクセス権限がありません</p>
          <p className="mt-2 text-sm text-red-600">この機能は管理者のみ利用できます。</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading size="medium" />
      </div>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <>
        <div className="p-6">
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <p className="text-gray-500">区分がありません</p>
            <Button onClick={handleAddCategory} color="primary" size="md" className="mt-4">
              区分を追加
            </Button>
          </div>
        </div>
        {/* 区分追加モーダル */}
        <AddCategoryModal
          isOpen={isAddModalOpen}
          onClose={handleCloseAddModal}
          onSubmit={handleSubmitNewCategory}
          isLoading={createCategoryMutation.isPending}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex min-h-full flex-col bg-gray-50">
        {/* ヘッダー：タブと追加ボタン */}
        <div className="bg-gray-50 px-6 pt-6">
          <CategoryTabs
            categories={categories}
            selectedCategoryId={selectedCategoryId}
            onSelectCategory={setSelectedCategoryId}
            onAddCategory={handleAddCategory}
          />
        </div>

        {/* メインコンテンツエリア */}
        <div className="flex-1 p-6">
          {selectedCategoryId ? (
            isLoadingDetail ? (
              <div className="flex h-full items-center justify-center">
                <Loading size="medium" />
              </div>
            ) : categoryDetail ? (
              <CustomFieldList
                categoryName={categoryDetail.name}
                fields={sortedFields}
                onAddField={handleAddField}
                onEditField={handleEditField}
                onDeleteField={handleDeleteField}
                onEditCategory={handleEditCategory}
                onDeleteCategory={handleDeleteCategory}
                onReorder={handleReorderFields}
                onMoveCategoryUp={handleMoveCategoryUp}
                onMoveCategoryDown={handleMoveCategoryDown}
                canMoveCategoryUp={getCategoryPosition().canMoveUp}
                canMoveCategoryDown={getCategoryPosition().canMoveDown}
                isReorderingCategory={reorderCategoriesMutation.isPending}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-red-500">カテゴリの読み込みに失敗しました</p>
              </div>
            )
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-gray-400">カテゴリを選択してください</p>
            </div>
          )}
        </div>
      </div>

      {/* 区分追加モーダル */}
      <AddCategoryModal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        onSubmit={handleSubmitNewCategory}
        isLoading={createCategoryMutation.isPending}
      />

      {/* 区分編集モーダル */}
      <EditCategoryModal
        isOpen={isEditModalOpen}
        categoryId={selectedCategoryId}
        initialName={categoryDetail?.name || ''}
        onClose={handleCloseEditModal}
        onSubmit={handleSubmitEditCategory}
        isLoading={updateCategoryMutation.isPending}
      />

      {/* カスタム項目追加モーダル */}
      <AddCustomFieldModal
        isOpen={isAddFieldModalOpen}
        onClose={handleCloseAddFieldModal}
        onSubmit={handleSubmitAddField}
        isLoading={createFieldMutation.isPending}
      />

      {/* カスタム項目編集モーダル */}
      <EditCustomFieldModal
        isOpen={isEditFieldModalOpen}
        field={fieldToEdit}
        onClose={handleCloseEditFieldModal}
        onSubmit={handleSubmitEditField}
        isLoading={updateFieldMutation.isPending}
      />

      {/* カスタム項目削除確認ダイアログ */}
      <DeleteCustomFieldDialog
        isOpen={!!fieldToDelete}
        field={fieldToDelete}
        isDeleting={deleteFieldMutation.isPending}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      {/* 区分削除ダイアログ */}
      <DeleteCustomCategoryDialog
        isOpen={!!categoryToDelete}
        categoryName={categoryToDelete?.name || null}
        isDeleting={deleteCategoryMutation.isPending}
        onClose={handleCancelDeleteCategory}
        onConfirm={handleConfirmDeleteCategory}
      />
    </>
  );
}
